import React from 'react'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { DirectLinkIntent } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customerProgram'
import {
  FORKED_TRIAGE_ENTRY_POINTS,
  PATHWAYS_ENTRY_POINTS,
  TRIAGE_ENTRY_POINTS,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import {
  COUPLES_PARAMS,
  createOnsiteTherapyParams,
  FAMILY_PARAMS,
  MEDS_PARAMS,
  PARENTING_PARAMS,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/speculativeConstant'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  ExtendedPathwayOption,
  PathwayOptions,
  PathwaysRedirectUrls,
} from '@lyrahealth-inc/shared-app-logic/src/features/pathways/constants'
import { DirectLinkSource } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'

import { useGoToDirectPathBooking } from '../../../common/hooks/useGoToDirectPathBooking'
import { useNavigateToSearchForCare } from '../../../common/hooks/useNavigateToSearchForCare'
import { useNavigateToSearchForCareChild } from '../../../common/hooks/useNavigateToSearchForCareChild'
import { getCustomerInfo } from '../../../data/customer/customerSelectors'
import { getId } from '../../../data/user/userSelectors'
import { getOnboardDirectLinkSource } from '../../../features/onboard/data/onboardSelectors'
import { getLoadingOnsiteProviders, getT1OnsiteProviders } from '../../onboard/data/t1-flow/t1Selectors'
import { setSelectedPathwayOption, setTriageEntryPoint } from '../../register/data/registerActions'
import ForkedTriageContainer from '../ForkedTriageContainer'
import { forkedTriageNavigationRoutes } from '../types'

type Props = ConnectedProps<typeof connector>

const ForkedTriage: React.FC<Props> = ({ setSelectedPathwayOption, setTriageEntryPoint }) => {
  const { state } = useLocation()
  const navigate = useNavigate()
  const isLoggedIn = Boolean(useSelector(getId))
  const navigateToSearchForCare = useNavigateToSearchForCare()
  const goToDirectPath = useGoToDirectPathBooking()
  const { navigateToChildSearch } = useNavigateToSearchForCareChild()
  const directLinkSource = useSelector(getOnboardDirectLinkSource) ?? DirectLinkSource.HOMEBASE_FORKED_TRIAGE
  const customerDisplayName = useSelector(getCustomerInfo)
  const { forkedTriageV2Enabled, isOnsiteEntrypointsEnabled } = useFlags()

  const onsiteProviders = useSelector(getT1OnsiteProviders)?.size
  const loadingOnsiteProviders = useSelector(getLoadingOnsiteProviders)
  const showPersistentOnsiteEntrypointSadPath =
    isOnsiteEntrypointsEnabled && !loadingOnsiteProviders && onsiteProviders === 0

  const setPathwayOptionAndNavigateToRegistration = (
    pathwayOption: ExtendedPathwayOption,
    mixpanelEntryPoint: FORKED_TRIAGE_ENTRY_POINTS,
  ) => {
    setSelectedPathwayOption(pathwayOption)
    navigate('/register/connect', {
      state: { ...state, mixpanelEntryPoint, origin: TRIAGE_ENTRY_POINTS.FORKED_TRIAGE },
    })
  }

  const navigateToRegistrationRoutes: forkedTriageNavigationRoutes = {
    onSelfSearchPress: () => {
      setPathwayOptionAndNavigateToRegistration(
        PathwayOptions.SELF_SEARCH,
        FORKED_TRIAGE_ENTRY_POINTS.FIND_CARE_FOR_SELF,
      )
    },
    onChildSearchPress: () => {
      setPathwayOptionAndNavigateToRegistration(
        PathwayOptions.CHILD_SEARCH,
        FORKED_TRIAGE_ENTRY_POINTS.FIND_CARE_FOR_CHILD,
      )
    },
    onCoachingToolkitPress: () => {
      setPathwayOptionAndNavigateToRegistration(
        PathwayOptions.COACHING_TOOLKIT,
        FORKED_TRIAGE_ENTRY_POINTS.COACHING_FOR_SELF,
      )
    },
    onParentingToolkitPress: () => {
      setPathwayOptionAndNavigateToRegistration(
        PathwayOptions.PARENTING_TOOLKIT,
        FORKED_TRIAGE_ENTRY_POINTS.COACHING_FOR_CHILD,
      )
    },
    onMedsConsultPress: () => {
      setPathwayOptionAndNavigateToRegistration(
        PathwayOptions.MEDICATION_CONSULTATION,
        FORKED_TRIAGE_ENTRY_POINTS.MEDICATION_CONSULTATION_FOR_SELF,
      )
    },
    onFamilyTherapyPress: () => {
      setPathwayOptionAndNavigateToRegistration(
        PathwayOptions.FAMILY_THERAPY,
        FORKED_TRIAGE_ENTRY_POINTS.FIND_CARE_FOR_FAMILY,
      )
    },
    onCouplesTherapyPress: () => {
      setPathwayOptionAndNavigateToRegistration(
        PathwayOptions.COUPLES_THERAPY,
        FORKED_TRIAGE_ENTRY_POINTS.FIND_CARE_FOR_COUPLES,
      )
    },
    // Onsite therapy is only available to logged-in members
    onOnsiteTherapyPress: () => {},
  }

  const triageEntryPoint =
    state?.mixpanelEntryPoint === PATHWAYS_ENTRY_POINTS.SEARCH_FOR_CARE_MODAL
      ? TRIAGE_ENTRY_POINTS.SEARCH_FOR_CARE_MODAL_FORKED_TRIAGE
      : TRIAGE_ENTRY_POINTS.FORKED_TRIAGE

  const navigateToLoggedInRoutes: forkedTriageNavigationRoutes = {
    onSelfSearchPress: () => {
      setTriageEntryPoint(triageEntryPoint)
      navigateToSearchForCare()
    },
    onChildSearchPress: () => {
      setTriageEntryPoint(triageEntryPoint)
      navigateToChildSearch()
    },
    onCoachingToolkitPress: () => {
      navigate(PathwaysRedirectUrls.COACHING_TOOLKIT, { state })
    },
    onParentingToolkitPress: () => {
      if (forkedTriageV2Enabled) {
        goToDirectPath({
          params: {
            ...PARENTING_PARAMS,
            directLinkSource,
          },
        })
      } else {
        navigate(PathwaysRedirectUrls.PARENTING_TOOLKIT, { state })
      }
    },
    onMedsConsultPress: () => {
      goToDirectPath({
        params: {
          ...MEDS_PARAMS,
          directLinkIntent: DirectLinkIntent.MEDS_CONSULT,
          directLinkSource,
        },
      })
    },
    onFamilyTherapyPress: () => {
      goToDirectPath({
        params: {
          ...FAMILY_PARAMS,
          directLinkIntent: DirectLinkIntent.FAMILY_SUPPORT,
          directLinkSource,
        },
      })
    },
    onCouplesTherapyPress: () => {
      goToDirectPath({
        params: {
          ...COUPLES_PARAMS,
          directLinkIntent: DirectLinkIntent.COUPLES_THERAPY,
          directLinkSource,
        },
      })
    },
    onOnsiteTherapyPress: () => {
      // TODO: Navigate to onsite-only location page from PROVREC-4878
      goToDirectPath({
        params: {
          ...createOnsiteTherapyParams({ customerName: customerDisplayName }),
          ...(!showPersistentOnsiteEntrypointSadPath && { directLinkIntent: DirectLinkIntent.ONSITE_THERAPY }),
          directLinkSource,
        },
      })
    },
  }

  return (
    <ForkedTriageContainer navigationRoutes={isLoggedIn ? navigateToLoggedInRoutes : navigateToRegistrationRoutes} />
  )
}

const connector = connect(null, {
  setSelectedPathwayOption,
  setTriageEntryPoint,
})

export default connector(ForkedTriage)
